/**
 * Positioning Compatibility Layer
 * Provides consistent positioning calculations for both CSS Grid and legacy pixel-based layouts
 */

import { differenceInMinutes } from 'date-fns';
import { DayShift, HourRange } from '@/types';
import { INTERVAL_MINUTES, INTERVALS_PER_HOUR } from '@/components/shifts/constants';

export interface PositionAndWidth {
  left: number;
  width: number;
  top?: number;
}

/**
 * Calculate pixel position and width for shifts and hour ranges
 * Works with both CSS Grid (1fr columns) and legacy pixel positioning
 */
export const getPositionAndWidth = (
  startTime: Date,
  endTime: Date,
  displayStartTime: Date,
  displayEndTime: Date,
  totalDisplayHours: number,
  intervalMinutes: number = INTERVAL_MINUTES,
  minutesPerHour: number = 60,
  hourCellWidthPx: number = 0, // No default - caller must provide valid measurement
  hideDayColumn: boolean = false
): PositionAndWidth => {
  // Input validation
  if (!startTime || !endTime || !displayStartTime || !displayEndTime) {
    // Invalid date objects provided
    console.warn('⚠️ positioning-compatibility-layer: Invalid date objects');
    return { left: 0, width: 0 };
  }

  if (totalDisplayHours <= 0 || hourCellWidthPx <= 0) {
    // Invalid display parameters - wait for proper measurement
    console.warn('⚠️ positioning-compatibility-layer: Invalid parameters - totalDisplayHours:', totalDisplayHours, 'hourCellWidthPx:', hourCellWidthPx);
    return { left: 0, width: 0 };
  }

  const startTimeOfDay = startTime.getHours() * 60 + startTime.getMinutes();
  const endTimeOfDay = endTime.getHours() * 60 + endTime.getMinutes();
  const displayStartTimeOfDay = displayStartTime.getHours() * 60 + displayStartTime.getMinutes();

  // Calculate time offsets using time-of-day only (ignores date component)
  const startOffsetMinutes = startTimeOfDay - displayStartTimeOfDay;
  let endOffsetMinutes = endTimeOfDay - displayStartTimeOfDay;

  // Handle overnight shifts (shifts that cross midnight)
  if (endTimeOfDay < startTimeOfDay) {
    // Shift crosses midnight - add 24 hours to end time
    endOffsetMinutes += 24 * 60;
  }

  // Handle negative offsets (shifts starting before display start time)
  const safeStartOffset = Math.max(0, startOffsetMinutes);
  const safeEndOffset = Math.max(safeStartOffset, endOffsetMinutes);

  // Calculate position based on intervals
  const intervalWidth = hourCellWidthPx;

  // Convert time to interval positions
  const startInterval = Math.floor(safeStartOffset / intervalMinutes);
  const endInterval = Math.ceil(safeEndOffset / intervalMinutes);

  // Calculate pixel positions
  // For absolutely positioned elements, we must manually add day column offset
  const dayColumnOffset = hideDayColumn ? 0 : 32;
  const left = dayColumnOffset + (startInterval * intervalWidth);
  const width = Math.max(intervalWidth * 0.5, (endInterval - startInterval) * intervalWidth);

  return {
    left: Math.round(left),
    width: Math.round(width),
    top: 0
  };
};

/**
 * Enhanced positioning service for complex layouts
 */
export const enhancedShiftPositioningService = {
  calculateHourRangePosition: (
    range: HourRange,
    displayStartTime: Date,
    displayEndTime: Date,
    totalHours: number,
    hourCellWidthPx: number,
    hideDayColumn: boolean = false
  ): PositionAndWidth => {
    return getPositionAndWidth(
      new Date(range.startTime),
      new Date(range.endTime),
      displayStartTime,
      displayEndTime,
      totalHours,
      INTERVAL_MINUTES,
      60,
      hourCellWidthPx,
      hideDayColumn
    );
  },

  calculateShiftPosition: (
    shift: DayShift,
    displayStartTime: Date,
    displayEndTime: Date,
    totalHours: number,
    hourCellWidthPx: number,
    hideDayColumn: boolean = false
  ): PositionAndWidth => {
    return getPositionAndWidth(
      shift.startTime,
      shift.endTime,
      displayStartTime,
      displayEndTime,
      totalHours,
      INTERVAL_MINUTES,
      60,
      hourCellWidthPx,
      hideDayColumn
    );
  }
};

/**
 * Grid-aware positioning that accounts for CSS Grid fractional units
 */
export interface GridAwarePosition {
  left: number;
  width: number;
  gridColumn?: string;
  gridRow?: number;
}

export const calculateGridAwarePosition = (
  startTime: Date,
  endTime: Date,
  displayStartTime: Date,
  totalDisplayHours: number,
  containerWidth: number,
  hideDayColumn: boolean = false
): GridAwarePosition => {
  // Calculate grid column positions
  const startOffsetMinutes = Math.max(0, differenceInMinutes(startTime, displayStartTime));
  const endOffsetMinutes = Math.max(0, differenceInMinutes(endTime, displayStartTime));
  
  const startInterval = Math.floor(startOffsetMinutes / INTERVAL_MINUTES);
  const endInterval = Math.ceil(endOffsetMinutes / INTERVAL_MINUTES);
  
  // Calculate total intervals
  const totalIntervals = Math.ceil(totalDisplayHours * INTERVALS_PER_HOUR);

  // Calculate actual pixel positions based on container width
  // Day column offset removed - CSS Grid handles column spacing
  const availableWidth = containerWidth;
  const intervalWidth = availableWidth / totalIntervals;

  const left = startInterval * intervalWidth;
  const width = Math.max(intervalWidth * 0.5, (endInterval - startInterval) * intervalWidth);
  
  // Generate CSS Grid column string
  // Day column is handled by CSS grid template, intervals always start at column 1 (hideDayColumn=true) or column 2 (hideDayColumn=false)
  const gridStartColumn = hideDayColumn ? startInterval + 1 : startInterval + 2;
  const gridEndColumn = hideDayColumn ? endInterval + 1 : endInterval + 2;
  const gridColumn = `${gridStartColumn} / ${gridEndColumn}`;
  
  return {
    left: Math.round(left),
    width: Math.round(width),
    gridColumn
  };
};

/**
 * Convert CSS Grid fractional units to pixel values
 */
export const gridFractionToPixels = (
  gridColumn: string,
  containerWidth: number,
  totalIntervals: number,
  hideDayColumn: boolean = false
): PositionAndWidth => {
  // Parse grid column string (e.g., "5 / 12" -> start: 5, end: 12)
  const match = gridColumn.match(/(\d+)\s*\/\s*(\d+)/);
  if (!match) {
    // Invalid grid column format
    return { left: 0, width: 100 };
  }
  
  const startColumn = parseInt(match[1], 10);
  const endColumn = parseInt(match[2], 10);

  // Calculate pixel positions
  // Day column offset removed - CSS Grid handles column spacing
  const availableWidth = containerWidth;
  const intervalWidth = availableWidth / totalIntervals;

  // Convert grid positions to pixels
  const gridStartIndex = startColumn - 1;
  const gridEndIndex = endColumn - 1;

  const left = gridStartIndex * intervalWidth;
  const width = (gridEndIndex - gridStartIndex) * intervalWidth;
  
  return {
    left: Math.round(left),
    width: Math.round(Math.max(intervalWidth * 0.5, width))
  };
};

export default {
  getPositionAndWidth,
  enhancedShiftPositioningService,
  calculateGridAwarePosition,
  gridFractionToPixels
};